<script setup>
/* global axios */
// [AstraChat] Configurações → Chaves Pix. CRUD das chaves Pix salvas da empresa,
// usadas no compositor do AstraCall (Cobrança Pix). Só administrador.
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const base = computed(
  () => `/api/v1/accounts/${route.params.accountId}/kanban/pix_keys`
);

const keys = ref([]);
const loading = ref(true);
const saving = ref(false);
const editing = ref(null); // objeto em edição/criação
const emptyKey = () => ({
  id: null,
  label: '',
  pix_key: '',
  merchant_name: '',
  merchant_city: '',
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base.value);
    keys.value = data?.data || [];
  } catch {
    keys.value = [];
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const openNew = () => {
  editing.value = emptyKey();
};
const openEdit = k => {
  editing.value = { ...k };
};
const cancel = () => {
  editing.value = null;
};

const save = async () => {
  if (!editing.value.pix_key.trim()) {
    useAlert(t('PIX_KEYS.KEY_REQUIRED'));
    return;
  }
  saving.value = true;
  try {
    const payload = { pix_key: editing.value };
    if (editing.value.id) {
      await axios.patch(`${base.value}/${editing.value.id}`, payload);
    } else {
      await axios.post(base.value, payload);
    }
    editing.value = null;
    await load();
    useAlert(t('PIX_KEYS.SAVED'));
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || t('PIX_KEYS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const remove = async k => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PIX_KEYS.REMOVE_CONFIRM', { name: k.label || k.pix_key })))
    return;
  try {
    await axios.delete(`${base.value}/${k.id}`);
    await load();
    useAlert(t('PIX_KEYS.REMOVED'));
  } catch {
    useAlert(t('PIX_KEYS.REMOVE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!keys.length && !editing"
    :no-records-message="t('PIX_KEYS.EMPTY')"
    :is-loading="loading"
    :loading-message="t('PIX_KEYS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('PIX_KEYS.TITLE')"
        :description="t('PIX_KEYS.DESC')"
      >
        <template #actions>
          <NextButton
            :label="t('PIX_KEYS.NEW')"
            icon="i-lucide-plus"
            size="sm"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- form de criação/edição -->
      <div
        v-if="editing"
        class="flex flex-col gap-3 p-4 mb-4 rounded-xl border border-n-weak bg-n-alpha-1"
      >
        <div class="grid grid-cols-2 gap-3">
          <label class="text-sm">
            <span class="text-n-slate-11">{{ t('PIX_KEYS.FORM.LABEL') }}</span>
            <input v-model="editing.label" class="pk-input" :placeholder="t('PIX_KEYS.FORM.LABEL_PLACEHOLDER')" />
          </label>
          <label class="text-sm">
            <span class="text-n-slate-11">{{ t('PIX_KEYS.FORM.KEY') }}</span>
            <input v-model="editing.pix_key" class="pk-input" :placeholder="t('PIX_KEYS.FORM.KEY_PLACEHOLDER')" />
          </label>
          <label class="text-sm">
            <span class="text-n-slate-11">{{ t('PIX_KEYS.FORM.MERCHANT') }}</span>
            <input v-model="editing.merchant_name" class="pk-input" :placeholder="t('PIX_KEYS.FORM.MERCHANT_PLACEHOLDER')" />
          </label>
          <label class="text-sm">
            <span class="text-n-slate-11">{{ t('PIX_KEYS.FORM.CITY') }}</span>
            <input v-model="editing.merchant_city" class="pk-input" :placeholder="t('PIX_KEYS.FORM.CITY_PLACEHOLDER')" />
          </label>
        </div>
        <div class="flex gap-2 justify-end">
          <NextButton :label="t('PIX_KEYS.FORM.CANCEL')" variant="faded" slate size="sm" @click="cancel" />
          <NextButton :label="t('PIX_KEYS.FORM.SAVE')" size="sm" :is-loading="saving" @click="save" />
        </div>
      </div>

      <!-- lista -->
      <div v-if="keys.length" class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="k in keys"
          :key="k.id"
          class="flex items-center gap-4 py-3"
        >
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ k.label || k.pix_key }}
            </span>
            <span class="text-xs text-n-slate-11 truncate">
              {{ k.pix_key }}
              <template v-if="k.merchant_name"> · {{ k.merchant_name }}</template>
              <template v-if="k.merchant_city"> · {{ k.merchant_city }}</template>
            </span>
          </div>
          <NextButton icon="i-lucide-pencil" slate faded sm @click="openEdit(k)" />
          <NextButton
            icon="i-lucide-trash-2"
            slate
            faded
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="remove(k)"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.pk-input {
  @apply block w-full mt-1 px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12 outline-none focus:border-n-brand;
}
</style>
